'use client'

import Link from 'next/link'
import styled from 'styled-components'

import { MainHeader } from '../../../shared/ui-kit/actual/headers/MainHeader'
import { LineBreaks } from '../../../shared/ui-kit/actual/components/LineBreaks'
import { dateToString } from '../../../shared/utils/dateToString'

type Article = {
  articleId?: string | number
  authorId?: string
  authorImageUrl?: string | null
  authorLogin?: string
  authorName?: string
  commentsCount?: number
  content?: string
  createdOn?: string
  imageUrl?: string | null
  sharesCount?: number
  title?: string
  viewsCount?: number
}

type RelatedArticle = {
  id?: string | number
  articleId?: string | number
  imageUrl?: string | null
  title?: string
  commentsCount?: number
  sharesCount?: number
  viewsCount?: number
  statistics?: {
    commentsCount?: number
    sharesCount?: number
    viewsCount?: number
  }
}

type AuthorProfile = {
  aboutItself?: string
  accountName?: string
  accountType?: string
  avatarLink?: string | null
  fullName?: string
  subscribersCount?: number
  subscriptionsCount?: number
  buyerRating?: number
  sellerRating?: number
}

type Props = {
  articleData: Article
  articleId: string
  authorProfile?: AuthorProfile
  isAuthenticated?: boolean
  relatedArticles?: RelatedArticle[]
}

const ratingColor = (accountType?: string) => {
  if (accountType === 'Premium') return '#f2bd24'
  if (accountType === 'Pro') return '#7156d9'
  return '#008c8c'
}

const authorTitle = (article: Article, profile?: AuthorProfile) =>
  profile?.fullName ||
  profile?.accountName ||
  article.authorName ||
  article.authorLogin ||
  'Пользователь'

const averageRating = (profile?: AuthorProfile) => {
  const buyer = profile?.buyerRating || 0
  const seller = profile?.sellerRating || 0
  const values = [buyer, seller].filter(Boolean)
  if (!values.length) return '0'
  return (values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1)
}

const ArticleStats = ({
  commentsCount = 0,
  sharesCount = 0,
  viewsCount = 0,
}: {
  commentsCount?: number
  sharesCount?: number
  viewsCount?: number
}) => (
  <Stats>
    <Stat>
      <img src="/images/interface/views-eye.svg" alt="" />
      {viewsCount}
    </Stat>
    <Stat>
      <img src="/images/interface/comment.svg" alt="" />
      {commentsCount}
    </Stat>
    <Stat>
      <img src="/images/interface/share.svg" alt="" />
      {sharesCount}
    </Stat>
  </Stats>
)

const AuthorSnippet = ({ article, profile }: { article: Article; profile?: AuthorProfile }) => (
  <AuthorSnippetWrapper>
    <SmallAvatar>
      {(profile?.avatarLink || article.authorImageUrl) && (
        <img
          src={profile?.avatarLink || article.authorImageUrl}
          alt={authorTitle(article, profile)}
        />
      )}
    </SmallAvatar>
    <div>
      <SnippetName>{authorTitle(article, profile)}</SnippetName>
      {(profile?.accountName || article.authorLogin) && (
        <SnippetLogin>@{profile?.accountName || article.authorLogin}</SnippetLogin>
      )}
    </div>
  </AuthorSnippetWrapper>
)

const ArticleContent = ({
  article,
  profile,
  showAuthor,
}: {
  article: Article
  profile?: AuthorProfile
  showAuthor?: boolean
}) => (
  <ArticleSection>
    {article.imageUrl ? (
      <ArticleCover>
        <img src={article.imageUrl} alt={article.title || 'Статья'} />
      </ArticleCover>
    ) : (
      <ArticleCover aria-hidden="true" />
    )}

    <ArticleBody>
      {showAuthor && <AuthorSnippet article={article} profile={profile} />}
      <MetaRow>
        <time dateTime={article.createdOn}>
          {article.createdOn ? dateToString(article.createdOn) : ''}
        </time>
        <ArticleStats
          viewsCount={article.viewsCount}
          commentsCount={article.commentsCount}
          sharesCount={article.sharesCount}
        />
      </MetaRow>
      <ArticleTitle>{article.title}</ArticleTitle>
      <ArticleText>
        <LineBreaks>{article.content || ''}</LineBreaks>
      </ArticleText>
    </ArticleBody>
  </ArticleSection>
)

const RelatedArticles = ({
  articles,
  mobile,
}: {
  articles: RelatedArticle[]
  mobile?: boolean
}) => {
  if (!articles.length) return null

  return (
    <RelatedSection>
      <RelatedTitle>Похожие статьи</RelatedTitle>
      <RelatedList $mobile={mobile}>
        {articles.map(article => {
          const id = article.id || article.articleId
          const stats = article.statistics || article

          return (
            <RelatedLink href={`/article/${id}`} key={id} $mobile={mobile}>
              <RelatedInfo>
                <RelatedArticleTitle>{article.title}</RelatedArticleTitle>
                <ArticleStats
                  viewsCount={stats.viewsCount}
                  commentsCount={stats.commentsCount}
                  sharesCount={stats.sharesCount}
                />
              </RelatedInfo>
              <RelatedCover $mobile={mobile}>
                {article.imageUrl && <img src={article.imageUrl} alt={article.title || 'Статья'} />}
              </RelatedCover>
            </RelatedLink>
          )
        })}
      </RelatedList>
    </RelatedSection>
  )
}

const AuthorSidebar = ({ article, profile }: { article: Article; profile?: AuthorProfile }) => (
  <Sidebar>
    <LargeAvatar>
      {(profile?.avatarLink || article.authorImageUrl) && (
        <img
          src={profile?.avatarLink || article.authorImageUrl}
          alt={authorTitle(article, profile)}
        />
      )}
      <Rating $color={ratingColor(profile?.accountType)}>
        <span>★</span>
        {averageRating(profile)}
      </Rating>
    </LargeAvatar>
    <SidebarName>{authorTitle(article, profile)}</SidebarName>
    {profile?.aboutItself && <About>{profile.aboutItself}</About>}
    <Followers>
      <div>
        <b>{profile?.subscriptionsCount || 0}</b>
        <span>Подписок</span>
      </div>
      <div>
        <b>{profile?.subscribersCount || 0}</b>
        <span>Подписчиков</span>
      </div>
    </Followers>
    <PrimaryButton type="button">Подписаться</PrimaryButton>
    <SecondaryButton type="button">Написать сообщение</SecondaryButton>
  </Sidebar>
)

const LoginSidebar = () => (
  <LoginPanel>
    <LoginTitle>Войдите в аккаунт</LoginTitle>
    <LoginText>Чтобы подписываться на авторов, общаться и сохранять интересные статьи</LoginText>
    <PrimaryLink href="/login">Войти</PrimaryLink>
    <SecondaryLink href="/registration">Зарегистрироваться</SecondaryLink>
  </LoginPanel>
)

export const ArticleServerView = ({
  articleData,
  articleId,
  authorProfile,
  isAuthenticated = false,
  relatedArticles = [],
}: Props) => (
  <Page>
    <DesktopOnly>
      <MainHeader />
      <BreadcrumbRow>
        <Breadcrumbs>
          <Link href="/">Главная</Link>
          <span>›</span>
          <Link href="/blog">Блог</Link>
          <span>›</span>
          <span>{articleData.title}</span>
        </Breadcrumbs>
        <IconButton type="button" aria-label="Поделиться статьёй">
          <img src="/images/buttons/share-primary.svg" alt="" />
        </IconButton>
      </BreadcrumbRow>

      <DesktopLayout>
        {isAuthenticated ? (
          <AuthorSidebar article={articleData} profile={authorProfile} />
        ) : (
          <LoginSidebar />
        )}
        <DesktopArticleColumn>
          <ArticleContent article={articleData} profile={authorProfile} />
          <RelatedArticles articles={relatedArticles} />
          <AllArticlesLink href="/blog">Все статьи</AllArticlesLink>
        </DesktopArticleColumn>
      </DesktopLayout>
    </DesktopOnly>

    <MobileOnly>
      <MobileHeader>
        <MobileBack href="/blog" aria-label="Вернуться к блогу">
          <img src="/images/buttons/arrow-left.svg" alt="" />
        </MobileBack>
        <MobileHeaderTitle>
          {authorProfile?.accountName ||
            articleData.authorLogin ||
            authorTitle(articleData, authorProfile)}
        </MobileHeaderTitle>
        <MobileActions>
          <IconButton type="button" aria-label="Пожаловаться">
            <img src="/images/buttons/report.svg" alt="" />
          </IconButton>
          <IconButton type="button" aria-label="Поделиться статьёй">
            <img src="/images/buttons/share-primary.svg" alt="" />
          </IconButton>
        </MobileActions>
      </MobileHeader>

      <MobileContent>
        <ArticleContent article={articleData} profile={authorProfile} showAuthor />
        <RelatedArticles articles={relatedArticles} mobile />
        <AllArticlesLink href="/blog">Все статьи</AllArticlesLink>
      </MobileContent>
      <MobileNavigation />
    </MobileOnly>
  </Page>
)

const MobileNavigation = () => (
  <BottomNavigation>
    <Link href="/orders">
      <img src="/images/bottom-menu/cart-gray.svg" alt="" />
      <span>Заказы</span>
    </Link>
    <Link href="/messages">
      <img src="/images/bottom-menu/chat-gray.svg" alt="" />
      <span>Сообщения</span>
    </Link>
    <Link href="/blog">
      <img src="/images/bottom-menu/blog-gray.svg" alt="" />
      <span>Блог</span>
    </Link>
    <Link href="/">
      <img src="/images/bottom-menu/main-gray.svg" alt="" />
      <span>Главная</span>
    </Link>
    <Link href="/profile">
      <img src="/images/bottom-menu/profile-gray.svg" alt="" />
      <span>Профиль</span>
    </Link>
  </BottomNavigation>
)

const Page = styled.main`
  width: min(100%, 1600px);
  margin: 0 auto;
  padding: 0 36px 70px;

  @media (max-width: 799px) {
    padding: 0 12px 90px;
  }
`

const DesktopOnly = styled.div`
  display: block;

  @media (max-width: 799px) {
    display: none;
  }
`

const MobileOnly = styled.div`
  display: none;

  @media (max-width: 799px) {
    display: block;
  }
`

const BreadcrumbRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 22px;
`

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
  color: #6e6e70;
  font-size: 14px;

  a {
    color: #6e6e70;
    text-decoration: none;
  }

  > span:last-child {
    overflow: hidden;
    max-width: 500px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const IconButton = styled.button`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
  }
`

const DesktopLayout = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 50px;
  width: 100%;
  margin-top: 36px;
`

const DesktopArticleColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  padding: 0 12px;
`

const ArticleSection = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 32px;
`

const ArticleCover = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  margin-bottom: 32px;
  border-radius: 12px;
  background: #f2f2f3;

  img {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: contain;
  }
`

const ArticleBody = styled.div`
  width: calc(100% - 16px);
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 12px;
  color: #99999b;
  font-size: 10px;
`

const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Stat = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #99999b;
  font-size: 10px;

  img {
    width: 16px;
    height: 16px;
  }
`

const ArticleTitle = styled.h1`
  margin: 0 0 8px;
  color: #252527;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
`

const ArticleText = styled.div`
  color: #6e6e70;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.04em;
  line-height: 171%;
`

const AuthorSnippetWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const SmallAvatar = styled.div`
  width: 44px;
  height: 44px;
  overflow: hidden;
  flex: 0 0 44px;
  border-radius: 10px;
  background: #f2f2f3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const SnippetName = styled.div`
  color: #252527;
  font-size: 14px;
  font-weight: 600;
`

const SnippetLogin = styled.div`
  margin-top: 3px;
  color: #99999b;
  font-size: 11px;
`

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 290px;
  max-width: 320px;
`

const LargeAvatar = styled.div`
  position: relative;
  width: 130px;
  height: 130px;
  margin-bottom: 35px;
  border-radius: 12px;
  background: #f2f2f3;

  > img {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: cover;
  }
`

const Rating = styled.div<{ $color: string }>`
  position: absolute;
  bottom: -22px;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 66px;
  padding: 7px 10px;
  transform: translateX(-50%);
  border-radius: 16px;
  background: ${({ $color }) => $color};
  color: #fff;
  font-size: 13px;
  font-weight: 600;

  span {
    font-size: 14px;
  }
`

const SidebarName = styled.h2`
  margin: 0 0 10px;
  color: #252527;
  font-size: 21px;
  font-weight: 600;
  text-align: center;
`

const About = styled.p`
  margin: 0 0 20px;
  color: #6e6e70;
  font-size: 16px;
  line-height: 1.45;
  text-align: center;
`

const Followers = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  margin: 8px 0 22px;

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  div + div {
    border-left: 1px solid #e5e5e7;
  }

  b {
    color: #252527;
    font-size: 16px;
  }

  span {
    color: #99999b;
    font-size: 12px;
  }
`

const PrimaryButton = styled.button`
  width: 100%;
  padding: 13px 15px;
  border: 0;
  border-radius: 15px;
  background: #008c8c;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
`

const SecondaryButton = styled(PrimaryButton)`
  margin-top: 8px;
  border: 1px solid #008c8c;
  background: #fff;
  color: #008c8c;
`

const LoginPanel = styled.aside`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 290px;
  width: 358px;
  gap: 8px;
`

const LoginTitle = styled.h2`
  margin: 0;
  color: #252527;
  font-size: 21px;
  font-weight: 600;
`

const LoginText = styled.p`
  margin: 0 0 12px;
  color: #6e6e70;
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
`

const PrimaryLink = styled(Link)`
  width: 100%;
  padding: 13px 15px;
  border-radius: 15px;
  background: #008c8c;
  color: #fff;
  font-size: 14px;
  text-align: center;
  text-decoration: none;
`

const SecondaryLink = styled(PrimaryLink)`
  border: 1px solid #008c8c;
  background: #fff;
  color: #008c8c;
`

const RelatedSection = styled.section`
  width: 100%;
  margin: 8px 0 16px;
`

const RelatedTitle = styled.h2`
  margin: 0 0 16px;
  color: #252527;
  font-size: 24px;
  font-weight: 700;

  @media (min-width: 1200px) {
    font-size: 36px;
  }
`

const RelatedList = styled.div<{ $mobile?: boolean }>`
  display: flex;
  flex-direction: ${({ $mobile }) => ($mobile ? 'column' : 'row')};
  flex-wrap: ${({ $mobile }) => ($mobile ? 'nowrap' : 'wrap')};
  gap: ${({ $mobile }) => ($mobile ? '16px' : '36px')};
  width: 100%;
`

const RelatedLink = styled(Link)<{ $mobile?: boolean }>`
  display: flex;
  flex: ${({ $mobile }) => ($mobile ? '1' : '0 1 350px')};
  flex-direction: ${({ $mobile }) => ($mobile ? 'row' : 'column-reverse')};
  gap: ${({ $mobile }) => ($mobile ? '12px' : '4px')};
  width: ${({ $mobile }) => ($mobile ? '100%' : '350px')};
  color: inherit;
  text-decoration: none;
`

const RelatedInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
`

const RelatedArticleTitle = styled.h3`
  display: -webkit-box;
  overflow: hidden;
  margin: 0 0 8px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #252527;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
`

const RelatedCover = styled.div<{ $mobile?: boolean }>`
  width: ${({ $mobile }) => ($mobile ? '100px' : '100%')};
  height: ${({ $mobile }) => ($mobile ? '100px' : '145px')};
  overflow: hidden;
  flex: ${({ $mobile }) => ($mobile ? '0 0 100px' : 'none')};
  border-radius: 18px;
  background: #f2f2f3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const AllArticlesLink = styled(Link)`
  display: block;
  width: min(300px, 100%);
  margin: 16px 0 32px;
  padding: 13px 15px;
  border: 1px solid #008c8c;
  border-radius: 15px;
  color: #008c8c;
  font-size: 14px;
  text-align: center;
  text-decoration: none;
`

const MobileHeader = styled.header`
  display: grid;
  grid-template-columns: 48px 1fr 64px;
  align-items: center;
  width: 100%;
  padding: 16px 0;
`

const MobileBack = styled(Link)`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;

  img {
    width: 24px;
    height: 24px;
  }
`

const MobileHeaderTitle = styled.div`
  overflow: hidden;
  color: #252527;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MobileActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`

const MobileContent = styled.div`
  width: 100%;
`

const BottomNavigation = styled.nav`
  position: fixed;
  z-index: 20;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-around;
  padding: 9px 8px calc(8px + env(safe-area-inset-bottom));
  border-top: 1px solid #eeeeef;
  background: #fff;

  a {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 52px;
    color: #99999b;
    font-size: 9px;
    text-decoration: none;
  }

  img {
    width: 22px;
    height: 22px;
  }
`
